import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock3, ChevronLeft, ChevronRight } from "lucide-react";
import { PortalShell } from "./PortalShell";
import { portalApi, portalErr } from "./portalApi";
import "../test/testTheme.css";

function QuizPicker({ onStart }) {
  const [data, setData] = useState(null);
  const [topicId, setTopicId] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    portalApi.get("/portal/quiz/topics").then(({ data: d }) => setData(d)).catch(() => setError("Could not load topics. Please refresh."));
  }, []);

  const start = async () => {
    if (!topicId) return;
    setError("");
    setLoading(true);
    try {
      const { data: d } = await portalApi.post("/portal/quiz/start", { topic_id: topicId, difficulty });
      onStart(d);
    } catch (err) { setError(portalErr(err)); } finally { setLoading(false); }
  };

  return (
    <div className="tp-wrap" style={{ maxWidth: 880, padding: "44px 22px 70px" }}>
      <div className="tp-eyebrow">DAILY QUIZ</div>
      <h1 className="tp-h2" style={{ fontSize: 32 }} data-testid="quiz-pick-title">Pick a topic, take 5 questions</h1>
      <p className="tp-muted">10 minutes on the clock. Randomised questions and options every attempt — practise as many times as you like.</p>
      {error && <p className="tp-error" role="alert" data-testid="quiz-pick-error">{error}</p>}
      {!data ? <p className="tp-muted">Loading topics…</p> : (
        <>
          <div style={{ margin: "16px 0" }}>
            <span className="tp-label" style={{ marginBottom: 8 }}>DIFFICULTY</span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="quiz-difficulties">
              <button type="button" className={difficulty === "" ? "tp-slot selected" : "tp-slot"} onClick={() => setDifficulty("")} data-testid="quiz-diff-any">Adaptive ✦</button>
              {data.difficulties.map((d) => <button type="button" key={d} className={difficulty === d ? "tp-slot selected" : "tp-slot"} onClick={() => setDifficulty(d)} data-testid={`quiz-diff-${d.toLowerCase()}`}>{d}</button>)}
            </div>
            <p className="tp-note" style={{ margin: "8px 0 0" }} data-testid="quiz-adaptive-note">Adaptive learns from your recent accuracy — it eases off when you struggle and pushes harder questions when you&apos;re scoring well. Pick a fixed level to override.</p>
          </div>
          <div className="tp-cards" data-testid="quiz-topics">
            {data.topics.map((t, i) => (
              <div className="tp-card tp-topic-card" key={t.topic_id} data-testid={`quiz-topic-${i}`}>
                <h3>{t.name}</h3>
                {t.description && <p>{t.description}</p>}
                <p className="tp-note" style={{ margin: "6px 0 12px" }}>{t.questions} questions in bank</p>
                {t.available
                  ? <button className={topicId === t.topic_id ? "tp-btn small" : "tp-btn ghost small"} onClick={() => setTopicId(t.topic_id)} data-testid={`quiz-select-${t.topic_id}`}>{topicId === t.topic_id ? "SELECTED ✓" : "SELECT"}</button>
                  : <p className="tp-note" style={{ color: "#b54708" }} data-testid={`quiz-unavailable-${i}`}>Not enough questions yet</p>}
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="tp-btn" disabled={!topicId || loading} onClick={start} data-testid="quiz-start">{loading ? "Pranjal Sir is finding questions for you… (up to ~30s)" : "START QUIZ"} <ArrowRight size={16} /></button>
          </div>
        </>
      )}
    </div>
  );
}

function QuizEngine({ quiz, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(quiz.answers || {});
  const [left, setLeft] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const offsetRef = useRef(quiz.server_now * 1000 - Date.now());
  const submittedRef = useRef(false);

  const doSubmit = async () => {
    setSubmitting(true);
    try {
      const { data } = await portalApi.post("/portal/quiz/submit", { attempt_id: quiz.attempt_id });
      onFinish(data.attempt_id);
    } catch { submittedRef.current = false; setSubmitting(false); setError("Could not submit. Check your connection and try again."); }
  };

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, Math.round((quiz.deadline * 1000 - (Date.now() + offsetRef.current)) / 1000));
      setLeft(remaining);
      if (remaining <= 0 && !submittedRef.current) { submittedRef.current = true; doSubmit(); }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const select = (qid, opt) => {
    setAnswers((c) => ({ ...c, [qid]: opt }));
    portalApi.post("/portal/quiz/answer", { attempt_id: quiz.attempt_id, question_id: qid, selected: opt }).catch(() => {});
  };

  const question = quiz.questions[idx];
  const answeredCount = Object.keys(answers).length;
  const mm = String(Math.floor((left ?? 0) / 60)).padStart(2, "0");
  const ss = String((left ?? 0) % 60).padStart(2, "0");

  return (
    <div className="tp-wrap" style={{ maxWidth: 820, paddingBottom: 60 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, flexWrap: "wrap", margin: "24px 0 14px" }}>
        <span className="tp-pill info" data-testid="quiz-topic-pill">{quiz.topic.name.toUpperCase()}</span>
        <span className={left !== null && left <= 60 ? "tp-timer low" : "tp-timer"} data-testid="quiz-timer"><Clock3 size={17} /> {mm}:{ss}</span>
        <span className="tp-pill info" data-testid="quiz-qcount">QUESTION {idx + 1} OF {quiz.questions.length}</span>
      </div>
      <div className="tp-progress" data-testid="quiz-progress"><i style={{ width: `${(answeredCount / quiz.questions.length) * 100}%` }} /></div>
      {error && <p className="tp-error" role="alert" data-testid="quiz-engine-error">{error}</p>}
      <div className="tp-qcard" data-testid="quiz-question-card" style={{ marginTop: 18 }}>
        <div className="tp-qmeta">{question.subtopic && <span className="tp-pill info">{question.subtopic.toUpperCase()}</span>}{question.difficulty && <span className="tp-pill warn">{question.difficulty.toUpperCase()}</span>}</div>
        <h3 data-testid="quiz-question-text">{question.text}</h3>
        {question.image_url && <img src={question.image_url} alt="Question diagram" />}
        {["A", "B", "C", "D"].map((key) => (
          <button type="button" key={key} className={answers[question.question_id] === key ? "tp-option selected" : "tp-option"} onClick={() => select(question.question_id, key)} data-testid={`quiz-option-${key}`}>
            <span className="key">{key}</span> {question.options[key]}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, gap: 12, flexWrap: "wrap" }}>
        <button className="tp-btn ghost small" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="quiz-prev"><ChevronLeft size={16} /> Previous</button>
        <div className="tp-qnav">{quiz.questions.map((q, i) => <button type="button" key={q.question_id} className={`${answers[q.question_id] ? "answered" : ""} ${i === idx ? "current" : ""}`} onClick={() => setIdx(i)} data-testid={`quiz-nav-${i}`}>{i + 1}</button>)}</div>
        {idx < quiz.questions.length - 1
          ? <button className="tp-btn secondary small" onClick={() => setIdx(idx + 1)} data-testid="quiz-next">Next <ChevronRight size={16} /></button>
          : <button className="tp-btn small" onClick={() => setConfirming(true)} data-testid="quiz-submit-open">Submit</button>}
      </div>
      <div style={{ textAlign: "center", marginTop: 22 }}><button className="tp-btn small" onClick={() => setConfirming(true)} data-testid="quiz-submit-bottom">SUBMIT QUIZ ({answeredCount}/{quiz.questions.length} answered)</button></div>
      {confirming && <div className="tp-modal-backdrop" data-testid="quiz-confirm-modal"><div className="tp-modal">
        <h3>Submit your quiz?</h3>
        <p className="tp-muted">You have answered {answeredCount} of {quiz.questions.length} questions. You can&apos;t change answers after submitting.</p>
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <button className="tp-btn" style={{ flex: 1 }} onClick={() => { submittedRef.current = true; setConfirming(false); doSubmit(); }} disabled={submitting} data-testid="quiz-submit-confirm">{submitting ? "Submitting…" : "Yes, submit"}</button>
          <button className="tp-btn ghost" style={{ flex: 1 }} onClick={() => setConfirming(false)} data-testid="quiz-submit-cancel">Keep answering</button>
        </div>
      </div></div>}
    </div>
  );
}

export function QuizResultView({ attemptId }) {
  const [attempt, setAttempt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    portalApi.get(`/portal/quiz/result/${attemptId}`).then(({ data }) => setAttempt(data)).catch((err) => setError(portalErr(err)));
  }, [attemptId]);

  if (error) return <div className="tp-wrap"><div className="tp-hero"><p className="tp-error" data-testid="quiz-result-error">{error}</p><Link className="tp-btn secondary" to="/portal/quiz">Back to quizzes</Link></div></div>;
  if (!attempt) return <div className="tp-wrap"><div className="tp-hero"><p className="tp-muted">Calculating your result…</p></div></div>;

  const r = attempt.result;
  return (
    <div className="tp-wrap" style={{ maxWidth: 880 }}>
      <section className="tp-score-hero">
        <div className="tp-eyebrow">QUIZ RESULT</div>
        <h1 data-testid="quiz-result-topic">{r.topic_name.toUpperCase()}</h1>
        <div className="tp-score-big" data-testid="quiz-score">{r.score}/{r.max_score}</div>
        <p className="tp-muted">{r.correct}/5 correct · {r.accuracy}% accuracy{attempt.difficulty ? ` · ${attempt.difficulty}` : ""}</p>
        {attempt.difficulty === "Adaptive" && attempt.target_mix && (
          <p className="tp-note" data-testid="quiz-adaptive-badge">Difficulty adapted to you{attempt.base_accuracy != null ? ` (recent accuracy ${Math.round(attempt.base_accuracy)}%)` : ""}: {["Easy", "Medium", "Hard"].filter((k) => attempt.target_mix[k]).map((k) => `${attempt.target_mix[k]} ${k}`).join(" · ")}</p>
        )}
        {r.level_up && (
          <p style={{ background: "#fff7e0", border: "1px solid #f2c14e", color: "#8a5a00", borderRadius: 12, padding: "12px 18px", fontWeight: 600, maxWidth: 560, margin: "14px auto 0" }} data-testid="quiz-level-up">
            🏆 Chapter mastered — you are Exam ready in {r.topic_name}! We&apos;ve emailed you a note. Time to pick your next chapter.
          </p>
        )}
        {r.eligible && <p className="tp-pill ok" data-testid="quiz-eligible">Reward-eligible score (4+/5) — rewards are granted under the published reward rules.</p>}
      </section>
      <div className="tp-score-grid" data-testid="quiz-score-grid">
        <div className="tp-stat"><b>{r.correct}/5</b><span>CORRECT</span></div>
        <div className="tp-stat"><b>{r.incorrect}/5</b><span>INCORRECT</span></div>
        <div className="tp-stat"><b>{r.unanswered}/5</b><span>UNANSWERED</span></div>
        <div className="tp-stat"><b>{r.accuracy}%</b><span>ACCURACY</span></div>
        <div className="tp-stat"><b>{Math.floor(r.time_taken_sec / 60)}m {String(r.time_taken_sec % 60).padStart(2, "0")}s</b><span>TIME TAKEN</span></div>
      </div>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", margin: "10px 0 30px" }}>
        <Link className="tp-btn" to="/portal/quiz" data-testid="quiz-retake">TAKE ANOTHER QUIZ <ArrowRight size={16} /></Link>
        <Link className="tp-btn secondary" to="/portal" data-testid="quiz-home">My dashboard</Link>
      </div>
      <section style={{ paddingBottom: 40 }}>
        <h2 className="tp-h2">Question-wise review</h2>
        <p className="tp-muted">Check the correct answers and explanations.</p>
        <div className="tp-analysis" data-testid="quiz-analysis">
          {r.analysis.map((q, i) => (
            <div className="tp-analysis-card" key={q.question_id} data-testid={`quiz-analysis-${i}`}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span className="tp-pill info">Q{i + 1}{q.subtopic ? ` · ${q.subtopic.toUpperCase()}` : ""}</span>
                {q.difficulty && <span className="tp-pill warn">{q.difficulty.toUpperCase()}</span>}
                <span className={`tp-pill ${q.status === "correct" ? "ok" : q.status === "incorrect" ? "bad" : ""}`}>{q.status === "correct" ? "CORRECT" : q.status === "incorrect" ? "INCORRECT" : "NOT ANSWERED"}</span>
              </div>
              <h4>{q.text}</h4>
              {q.image_url && <img src={q.image_url} alt="Question diagram" />}
              <div className="tp-analysis-options">
                {["A", "B", "C", "D"].map((key) => <div key={key} className={key === q.correct_answer ? "correct" : key === q.selected ? "picked-wrong" : ""}><b>{key}.</b> {q.options[key]}</div>)}
              </div>
              <p className="tp-muted" style={{ fontSize: 12 }}>Your answer: <b>{q.selected ? `Option ${q.selected}` : "Not attempted"}</b> · Correct answer: <b>Option {q.correct_answer}</b></p>
              {q.explanation && <div className="tp-expl" data-testid={`quiz-explanation-${i}`}><b>Explanation</b><br />{q.explanation}</div>}
              {q.solution && <div className="tp-expl"><b>Detailed solution</b><br />{q.solution}</div>}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function PortalQuizInner() {
  const [quiz, setQuiz] = useState(null);
  const [doneId, setDoneId] = useState(null);
  const navigate = useNavigate();

  if (quiz && !doneId) return <QuizEngine quiz={quiz} onFinish={(id) => { setDoneId(id); navigate(`/portal/quiz/result/${id}`); }} />;
  return <QuizPicker onStart={setQuiz} />;
}

export default function PortalQuiz() {
  return <PortalShell><PortalQuizInner /></PortalShell>;
}
